import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { addListingDetailsType, ListingDetailsType } from '~addListing/types';
import { useFormContext } from 'react-hook-form';
import {
  PAYMENT_METHOD_INSTALLMENT_KEY,
  RESALE_ID,
} from '~addListing/utils/data';
import InstallmentYearsPill from '~addListing/pages/PriceStep/InstallmentYearsPill';
import clampMonths from '~addListing/pages/PriceStep/utils/clampMonths';
import getDurationInDays from '~addListing/pages/PriceStep/utils/getDurationInDays';
import getInstallmentDetails from '~addListing/pages/PriceStep/utils/getInstallmentDetails';
import {
  DAYS_IN_YEAR,
  MAX_MONTHS,
  MAX_YEARS,
  MIN_MONTHS,
  MIN_YEARS,
  MONTHS_IN_YEAR,
  MONTHS_STEP,
  NUMBER_DAYS_IN_MONTH,
  YEARS_STEP,
} from '~addListing/pages/PriceStep/utils/constants';

const InstallmentsUnit = ({ listing }: { listing: ListingDetailsType }) => {
  const { t, i18n } = useTranslation('app');
  const isRtl = i18n.language !== 'en';
  const defaultInstallmentsDuration =
    listing?.payment_plans?.[0]?.duration || 0;
  const isResale = listing?.market_property_type === RESALE_ID;

  const {
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<addListingDetailsType>();

  const paymentPlanPrice = watch('paymentPlans')?.[0]?.price || 0;

  const downPaymentType = watch('downPaymentType') || 'amount';
  const paymentPlans = watch('paymentPlans') || [];
  const installmentPeriodType = watch('installmentPeriodType') || 'years';

  const { displayMonths, displayYears, installmentMonths, monthlyInstallment } =
    getInstallmentDetails({
      downPaymentType,
      downPayment: paymentPlans?.[0]?.downPayment,
      duration: paymentPlans?.[0]?.duration,
      installmentPeriodType,
      overPrice: paymentPlans?.[0]?.overPrice,
      price: paymentPlans?.[0]?.price || 0,
    });
  const hasFullData = paymentPlanPrice && installmentMonths;

  const paymentMethod = watch('paymentMethod');
  const paymentMethodInstallment =
    paymentMethod === PAYMENT_METHOD_INSTALLMENT_KEY;
  // The years/months filter is only available for the installment payment method,
  // otherwise the period is always expressed in years.
  const hasPeriodTypeFilter = paymentMethodInstallment;
  const isByMonthType = installmentPeriodType === 'months';
  const selectedFilterValue = isByMonthType ? displayMonths : displayYears;
  const countButtonClass = 'flex min-w-[38px] items-center gap-2';
  // Percentage of the track that should be filled (selected portion).
  const fillPercentage = isByMonthType
    ? (displayMonths / MAX_MONTHS) * 100
    : ((displayYears - MIN_YEARS) / (MAX_YEARS - MIN_YEARS)) * 100;

  const handleInstallmentChange = (
    value: number,
    type = installmentPeriodType,
  ) => {
    const updatedPlans = [...paymentPlans];

    updatedPlans[0] = {
      ...updatedPlans[0],
      duration: getDurationInDays(value, type),
    };
    setValue('paymentPlans', updatedPlans, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const onFilterChange = (type: 'years' | 'months') => {
    const installmentValue =
      type === 'months'
        ? clampMonths(displayYears * MONTHS_IN_YEAR)
        : displayYears;

    setValue('installmentPeriodType', type);
    handleInstallmentChange(installmentValue, type);
  };

  useEffect(() => {
    if (!defaultInstallmentsDuration) return;

    const defaultDurationInMonths =
      defaultInstallmentsDuration / NUMBER_DAYS_IN_MONTH;
    const defaultPeriodType =
      paymentMethodInstallment && defaultDurationInMonths < MAX_MONTHS
        ? 'months'
        : 'years';
    const defaultInstallmentValue =
      defaultPeriodType === 'months'
        ? clampMonths(defaultDurationInMonths)
        : defaultInstallmentsDuration / DAYS_IN_YEAR;

    setValue('installmentPeriodType', defaultPeriodType, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    handleInstallmentChange(defaultInstallmentValue, defaultPeriodType);
  }, [defaultInstallmentsDuration, paymentMethodInstallment]);

  const error = errors.paymentPlans?.[0]?.duration
    ? errors.paymentPlans?.[0]?.duration.message ===
      'durationMustBeLessThan30Years'
      ? t('durationMustBeLessThan30Years')
      : isResale
        ? t('remainingInstallmentPeriodRequired')
        : t('installmentsYearsRequired')
    : '';

  return (
    <div className="flex flex-col gap-1.5x w-full">
      <div className="flex items-center justify-between gap-x mb-0.5x">
        <span className="text-body-1 text-gray__dark_2">
          {paymentMethodInstallment
            ? t('remainingInstallmentDuration')
            : t('installmentPeriod')}
        </span>
        {hasPeriodTypeFilter ? (
          <div className="flex flex-col gap-3">
            <div className="flex gap-3x">
              <button
                type="button"
                className={`${countButtonClass} ${
                  isByMonthType
                    ? 'text-gray__dark_1 text-body_2'
                    : 'text-blue text-title-5'
                }`}
                onClick={() => onFilterChange('years')}
              >
                {!isByMonthType && (
                  <span className="flex h-[24px] w-[35px] items-center justify-center rounded-full bg-blue/10 px-2 py-1 text-blue text-caption-1">
                    {selectedFilterValue}
                  </span>
                )}
                {t('years')}
              </button>
              <span className="h-[24px] w-[2px] bg-gray"></span>
              <button
                type="button"
                className={`${countButtonClass} justify-center ${
                  isByMonthType
                    ? 'text-blue text-title-5'
                    : 'text-gray__dark_1 text-body_2'
                }`}
                onClick={() => onFilterChange('months')}
              >
                {isByMonthType && (
                  <span className="flex h-[24px] w-[35px] items-center justify-center rounded-full bg-blue/10 px-2 py-1 text-blue text-caption-1">
                    {selectedFilterValue}
                  </span>
                )}
                {t('months')}
              </button>
            </div>
            <div
              className={`w-full flex rounded-[8px] bg-gray ${
                isByMonthType ? 'justify-end' : 'justify-start'
              }`}
            >
              <span className="h-[2px] w-[95px] rounded-[8px] bg-blue"></span>
            </div>
          </div>
        ) : (
          <InstallmentYearsPill years={displayYears} />
        )}
      </div>
      <div className="w-full">
        <div className="relative h-[24px] w-full select-none">
          {/* track */}
          <div className="absolute inset-x-0 top-1/2 h-[8px] -translate-y-1/2 rounded bg-gray" />
          {/* selected (filled) portion */}
          <div
            className="absolute top-1/2 h-[8px] -translate-y-1/2 rounded bg-blue"
            style={{ width: `${fillPercentage}%`, insetInlineStart: 0 }}
          />
          {/* thumb */}
          <div
            className="absolute top-1/2 h-[24px] w-[24px] -translate-y-1/2 rounded-full border-2 border-blue bg-white shadow-drop"
            style={{ insetInlineStart: `calc(${fillPercentage}% - 12px)` }}
          />
          {/* interactive native range input (transparent overlay) */}
          <input
            type="range"
            min={isByMonthType ? MIN_MONTHS : MIN_YEARS}
            max={isByMonthType ? MAX_MONTHS : MAX_YEARS}
            step={isByMonthType ? MONTHS_STEP : YEARS_STEP}
            dir={isRtl ? 'rtl' : 'ltr'}
            value={isByMonthType ? displayMonths : displayYears}
            onChange={(e) => handleInstallmentChange(Number(e.target.value))}
            onFocus={() => clearErrors('paymentPlans.0.duration')}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label={t('installmentPeriod')}
          />
        </div>
      </div>

      {Boolean(hasFullData) && (
        <p className="flex gap-1">
          <span className="text-body_2 text-orange_dark_1">
            {t('monthlyInstallment')}:
          </span>
          <span className="text-body_2 text-orange_dark_1">
            {Number(monthlyInstallment.toFixed(0)).toLocaleString()}{' '}
            {t('abbreviationEgyptianPound')}/ {t('month')}
          </span>
        </p>
      )}
      {error && <p className="text-body_2 text-red_dark">{error}</p>}
    </div>
  );
};

export default InstallmentsUnit;
